import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useInteractionStore } from '../interaction/InteractionStore';
import { getMetricsPoller, useMetricsStore } from '../MetricsPollerService';

export interface GatewaySystemMetrics {
    timestamp: number;
    uptimeSeconds: number;
    cpu: {
        count: number | null;
        loadAverage: {
            '1m': number | null;
            '5m': number | null;
            '15m': number | null;
        };
    };
    memory: {
        totalBytes: number | null | undefined;
        availableBytes: number | null | undefined;
        freeBytes: number | null | undefined;
        usedBytes: number | null | undefined;
        usagePercent: number | null | undefined;
        totalMegabytes: number | null | undefined;
        availableMegabytes: number | null | undefined;
        usedMegabytes: number | null | undefined;
    };
    process: {
        pid: number | null;
        rssBytes: number | null | undefined;
        rssMegabytes: number | null | undefined;
        cpuTimes: {
            user: number | null | undefined;
            system: number | null | undefined;
        };
    };
    environment: {
        hostname: string | null;
        platform: string | null;
        platformVersion: string | null;
    };
}

export interface HiveCoreEvent {
    t: string;
    at: string;
    [key: string]: any;
}

export interface HiveCoreState {
    workers: number | null; // null = unknown/not yet received, 0 = idle
    queueDepth: number | null; // null = unknown/not yet received
    workersLastSeenAt: string | null; // ISO timestamp of last workers metric update
    cpu: number;
    memory: number;
    buildstacks: Array<{ name: string; status: string; lastRun: string }>;
    bees: Array<{ name: string; status: string; lastRun?: string }>;
    connected: boolean;
    lastUpdate: string | null;
    systemMetrics: GatewaySystemMetrics | null;
    metricsLoading: boolean;
    metricsError: string | null;
}

/**
 * Sanitize an object for debug display by redacting sensitive keys.
 * This ensures no secrets (tokens, cookies, API keys) leak into the debug inspector.
 */
const SENSITIVE_KEYS = new Set([
    'token',
    'jwt',
    'authorization',
    'cookie',
    'set-cookie',
    'secret',
    'apikey',
    'api_key',
    'password',
    'credential',
    'auth',
    'bearer',
    'session',
    'refresh_token',
    'access_token',
]);

function sanitizeForDebug(obj: unknown, maxDepth = 5): unknown {
    if (maxDepth <= 0) return '[MAX_DEPTH]';
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeForDebug(item, maxDepth - 1));
    }
    if (typeof obj === 'object') {
        const sanitized: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj)) {
            const lowerKey = key.toLowerCase();
            if (SENSITIVE_KEYS.has(lowerKey) || SENSITIVE_KEYS.has(lowerKey.replace(/_/g, ''))) {
                sanitized[key] = '[REDACTED]';
            } else {
                sanitized[key] = sanitizeForDebug(value, maxDepth - 1);
            }
        }
        return sanitized;
    }
    return String(obj);
}

/**
 * Debug state for telemetry inspector (dev-only, gated by VITE_TELEMETRY_DEBUG=true)
 * Captures the last few telemetry events to diagnose Active Executors issues
 */
export interface TelemetryDebugState {
    lastSceneEventAt: string | null;
    lastScenePayload: string | null; // JSON string, max 500 chars
    lastWorkersKey: string | null; // The actual key received (hive.workers, hive.workers.active, etc)
    lastWorkersValue: number | null;
    lastQueueKey: string | null;
    lastQueueValue: number | null;
    sceneEventsReceived: number; // Total count of scene events received
    workersEventsReceived: number; // Count of events that contained workers metric
}

export interface UseHiveCoreEventsOptions {
    wsUrl?: string; // Deprecated - use baseUrl instead
    baseUrl?: string;
    wsPath?: string;
    namespace?: string;
    jwt: string;
    sceneId?: string;
    profile?: 'desktop' | 'mobile';
    onEvent?: (event: HiveCoreEvent) => void;
    onConnect?: () => void;
    onDisconnect?: () => void;
    reconnectDelay?: number;
    maxReconnectAttempts?: number;
    metricsUrl?: string;
    metricsBaseUrl?: string;
    metricsPollIntervalMs?: number;
    debug?: boolean;
}

export function useHiveCoreEvents(options: UseHiveCoreEventsOptions) {
    const {
        wsUrl: _wsUrl, // Deprecated - kept for backward compatibility
        baseUrl: providedBaseUrl,
        wsPath: providedWsPath,
        namespace: providedNamespace,
        jwt,
        sceneId = 'hivecore.control',
        profile = 'desktop',
        onEvent,
        onConnect,
        onDisconnect,
        reconnectDelay = 2000,
        maxReconnectAttempts = 5,
        debug = false,
    } = options;

    const env = import.meta.env as Record<string, string | undefined>;
    const baseUrl =
        providedBaseUrl ||
        env.VITE_HIVECORE_API_URL ||
        env.VITE_BACKEND_URL ||
        'https://hivecore-backend-production.up.railway.app';

    const wsPath = providedWsPath || env.VITE_HIVECORE_WS_PATH || '/socket.io';
    const namespace = providedNamespace || env.VITE_HIVECORE_NS || '/abda';

    const log = (...args: any[]) => debug && console.log('[HiveCore]', ...args);

    const [state, setState] = useState<HiveCoreState>({
        workers: null, // null = unknown/not yet received
        queueDepth: null, // null = unknown/not yet received
        workersLastSeenAt: null,
        cpu: 0,
        memory: 0,
        buildstacks: [],
        bees: [],
        connected: false,
        lastUpdate: null,
        systemMetrics: null,
        metricsLoading: false,
        metricsError: null,
    });

    // Debug state for telemetry inspector (dev-only, gated by VITE_TELEMETRY_DEBUG=true)
    const telemetryDebugEnabled =
        (import.meta.env as Record<string, string | undefined>).VITE_TELEMETRY_DEBUG === 'true';
    const [debugState, setDebugState] = useState<TelemetryDebugState>({
        lastSceneEventAt: null,
        lastScenePayload: null,
        lastWorkersKey: null,
        lastWorkersValue: null,
        lastQueueKey: null,
        lastQueueValue: null,
        sceneEventsReceived: 0,
        workersEventsReceived: 0,
    });

    const socketRef = useRef<Socket | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const { setSuccess: _setSuccess, setError: _setError, clearByIntentId } = useInteractionStore();

    // Note: metricsUrl and metricsPollIntervalMs options are deprecated
    // Metrics polling is now handled by the centralized MetricsPollerService
    // which uses a 60s interval with exponential backoff on 429

    const connect = () => {
        if (socketRef.current?.connected) {
            return;
        }

        const socketUrl = `${baseUrl.replace(/\/+$/, '')}${namespace}`;

        log('Connecting to Socket.IO', {
            url: socketUrl,
            path: wsPath,
            sceneId,
            profile,
            jwtLength: jwt.length,
        });

        const socket = io(socketUrl, {
            path: wsPath,
            transports: ['websocket', 'polling'], // Try websocket first, fallback to polling
            withCredentials: true,
            auth: {
                token: jwt,
                sceneId,
                profile,
            },
            reconnection: true,
            reconnectionDelay: reconnectDelay,
            reconnectionAttempts: maxReconnectAttempts,
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            log('Connected to Socket.IO', { id: socket.id });
            reconnectAttemptsRef.current = 0;

            const helloMsg = {
                t: 'hello',
                jwt,
                sceneId,
                profile,
                caps: {
                    w: window.innerWidth,
                    h: window.innerHeight,
                    dpr: window.devicePixelRatio || 1,
                    pointer: matchMedia('(pointer: coarse)').matches ? 'touch' : 'mouse',
                },
            };
            log('Sending hello:', { ...helloMsg, jwt: `${jwt.substring(0, 20)}...` });
            socket.emit('hello', helloMsg);

            setState(prev => ({ ...prev, connected: true }));
            onConnect?.();
        });

        socket.on('connect_error', err => {
            console.error('[HiveCore] Socket.IO connect_error:', err.message);
            setState(prev => ({ ...prev, connected: false }));
        });

        socket.on('disconnect', reason => {
            log('Disconnected from Socket.IO', { reason });
            setState(prev => ({ ...prev, connected: false }));
            onDisconnect?.();
        });

        socket.on('scene', (message: any) => {
            try {
                const scene = message.body || message;
                const newState: Partial<HiveCoreState> = {};

                // Debug: capture telemetry for inspector (only when enabled)
                let debugWorkersKey: string | null = null;
                let debugWorkersValue: number | null = null;
                let debugQueueKey: string | null = null;
                let debugQueueValue: number | null = null;

                if (scene.widgets) {
                    for (const widget of scene.widgets) {
                        if (widget.kind === 'metric' && widget.value !== undefined) {
                            // Accept both 'hive.workers' and 'hive.workers.active' for resilience
                            // Different backend services may use different key names
                            if (
                                widget.key === 'hive.workers' ||
                                widget.key === 'hive.workers.active'
                            ) {
                                newState.workers = widget.value;
                                newState.workersLastSeenAt = new Date().toISOString();
                                debugWorkersKey = widget.key;
                                debugWorkersValue = widget.value;
                            }
                            // Accept both 'hive.queueDepth' and 'hive.queue' for resilience
                            if (widget.key === 'hive.queueDepth' || widget.key === 'hive.queue') {
                                newState.queueDepth = widget.value;
                                debugQueueKey = widget.key;
                                debugQueueValue = widget.value;
                            }
                        }
                        if (widget.kind === 'gauge' && widget.value !== undefined) {
                            if (widget.key === 'hive.cpu') newState.cpu = widget.value;
                        }
                        if (widget.kind === 'table' && widget.rows) {
                            if (widget.key === 'buildstacks') {
                                newState.buildstacks = widget.rows.map((row: any[]) => ({
                                    name: row[0],
                                    status: row[1],
                                    lastRun: row[2],
                                }));
                            }
                        }
                    }
                }

                setState(prev => ({
                    ...prev,
                    ...newState,
                    lastUpdate: new Date().toISOString(),
                }));

                // Update debug state if telemetry debug is enabled
                if (telemetryDebugEnabled) {
                    // Sanitize payload before display to prevent leaking secrets
                    const sanitizedScene = sanitizeForDebug(scene);
                    const payloadStr = JSON.stringify(sanitizedScene).slice(0, 500);
                    setDebugState(prev => ({
                        lastSceneEventAt: new Date().toISOString(),
                        lastScenePayload: payloadStr,
                        lastWorkersKey: debugWorkersKey ?? prev.lastWorkersKey,
                        lastWorkersValue: debugWorkersValue ?? prev.lastWorkersValue,
                        lastQueueKey: debugQueueKey ?? prev.lastQueueKey,
                        lastQueueValue: debugQueueValue ?? prev.lastQueueValue,
                        sceneEventsReceived: prev.sceneEventsReceived + 1,
                        workersEventsReceived:
                            prev.workersEventsReceived + (debugWorkersKey ? 1 : 0),
                    }));
                }
            } catch (err) {
                console.error('[HiveCore] Error processing scene:', err);
            }
        });

        socket.on('heartbeat', () => {
            log('Heartbeat received');
        });

        socket.on('toast', (message: any) => {
            log(`${message.level}: ${message.msg}`);
        });

        socket.on('ack', (message: any) => {
            if (message.cid) {
                log('Intent acknowledged:', message.cid);
            }
        });

        socket.on('result', (message: any) => {
            if (message.cid) {
                log('Intent result:', message.cid, message.ok ? 'success' : 'error');
                clearByIntentId(message.cid);
                if (typeof (window as any).__handleActionResult === 'function') {
                    (window as any).__handleActionResult(message.cid, message.ok);
                }
            }
        });

        socket.onAny((eventName, ...args) => {
            const message = args[0];
            if (message && typeof message === 'object' && message.t) {
                onEvent?.(message);
            }
        });
    };

    // Subscribe to centralized metrics store instead of per-component polling
    // This eliminates 429 rate limiting by using a single shared poller
    const metricsStore = useMetricsStore();

    // Start the centralized metrics poller on mount
    useEffect(() => {
        const poller = getMetricsPoller();
        poller.start(baseUrl);

        // No cleanup needed - poller is a singleton that persists across components
        // It will pause automatically when tab is hidden
    }, [baseUrl]);

    // Sync metrics store data to local state for backward compatibility
    useEffect(() => {
        const monitoringMetrics = metricsStore.monitoringMetrics;
        if (monitoringMetrics) {
            const normalizedCpu = (() => {
                const load = monitoringMetrics.cpu?.loadAverage?.['1m'];
                const cores = monitoringMetrics.cpu?.count ?? 0;
                if (typeof load !== 'number' || !cores) {
                    return null;
                }
                return Math.max(0, Math.min(load / cores, 1));
            })();

            const memoryUsage = (() => {
                const percent = monitoringMetrics.memory?.usagePercent;
                if (typeof percent !== 'number') {
                    return null;
                }
                return Math.max(0, Math.min(percent / 100, 1));
            })();

            setState(prev => ({
                ...prev,
                cpu: normalizedCpu ?? prev.cpu,
                memory: memoryUsage ?? prev.memory,
                systemMetrics: monitoringMetrics,
                metricsLoading: metricsStore.monitoringLoading,
                metricsError: metricsStore.monitoringError,
            }));
        }
    }, [
        metricsStore.monitoringMetrics,
        metricsStore.monitoringLoading,
        metricsStore.monitoringError,
    ]);

    const disconnect = () => {
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
    };

    const sendIntent = (name: string, args: any = {}, widgetKey?: string) => {
        if (socketRef.current?.connected) {
            const cid = `c_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
            const intentMsg = {
                t: 'intent',
                cid,
                name,
                args: { ...args, widgetKey },
                ts: Date.now(),
            };
            log('Sending intent:', intentMsg);
            socketRef.current.emit('intent', intentMsg);
            return cid;
        } else {
            console.error('[HiveCore] Cannot send intent: not connected');
            return null;
        }
    };

    useEffect(() => {
        connect();
        return () => disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseUrl, wsPath, namespace, jwt, sceneId, profile]);

    return {
        state,
        connected: state.connected,
        sendIntent,
        reconnect: connect,
        disconnect,
        // Debug state for telemetry inspector (dev-only)
        telemetryDebugEnabled,
        debugState,
    };
}
